import { computed, ErrorHandler, inject, Injectable, Injector, type Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import {
	catchError,
	distinctUntilChanged,
	EMPTY,
	map,
	merge,
	type Observable,
	of,
	startWith,
	Subject,
	switchMap,
} from 'rxjs';

import { DesignChangePendingService, DesignChangeRepository } from '@efficiently/client-api-design-changes';
import { ProjectDetailSelectors } from '@efficiently/client-domain-project-detail';

const NO_PENDING_IDS: ReadonlySet<string> = new Set<string>();

/**
 * A read request, tagged with the one thing the two triggers differ in: whether the project itself
 * changed. Everything else about them is identical, which is why this carries a flag rather than
 * two separate pipelines.
 */
type PendingRead = {
	readonly projectId: string;
	readonly isProjectChange: boolean;
};

/**
 * The pending set has one job it must not fail at: it must never go blank while an answer it already
 * holds is still true. That is not cosmetic — `isPending` also gates approve and reject on the bulk,
 * location and selection surfaces, so a blank moment re-enables actions the server would refuse.
 *
 * Hence three transitions, differing only in what is shown *before* the read resolves:
 *
 * | Trigger | Before the read resolves | Resolved | Failed |
 * |---|---|---|---|
 * | Project change, first load included | the empty set | the new set | reported, the empty set stays |
 * | `refresh()` | the previous set | the new set | reported, the previous set stays |
 * | No project | the empty set, no read | — | — |
 *
 * A project change blanks because project B must never show project A's marks. **A refresh must not
 * blank.** A refresh follows every capture, so blanking there emptied the whole project's set for one
 * round trip on every single capture — the defect this shape exists to prevent.
 *
 * A failure keeps whatever the trigger already established, which falls out of the same rule: the
 * read reports to `ErrorHandler` and then emits nothing at all, so the signal holds its last value —
 * the empty set after a project change, the previous set after a refresh.
 */
@Injectable()
export class DefaultDesignChangePendingService extends DesignChangePendingService {
	private readonly repository = inject(DesignChangeRepository);
	private readonly errorReporter = inject(ErrorHandler);
	private readonly refreshSubject = new Subject<void>();

	private readonly store = inject(Store);
	private readonly injector = inject(Injector);

	/**
	 * Declared `string` by the selector, but the state carries no default — outside a loaded project it
	 * reads `undefined`. Hence the guard below rather than trust in the type.
	 */
	private readonly projectId: Signal<string> = this.store.selectSignal(ProjectDetailSelectors.projectId);

	private readonly projectId$ = merge(
		of(this.projectId()),
		toObservable(this.projectId, { injector: this.injector }),
	).pipe(distinctUntilChanged());

	/**
	 * The refresh is mapped through the current project so that a refresh with no project cannot walk
	 * past the guard in {@link readFor}.
	 */
	private readonly pendingReads$: Observable<PendingRead> = merge(
		this.projectId$.pipe(map((projectId): PendingRead => ({ projectId, isProjectChange: true }))),
		this.refreshSubject.pipe(map((): PendingRead => ({ projectId: this.projectId(), isProjectChange: false }))),
	);

	public override readonly pendingProjectFinishIds: Signal<ReadonlySet<string>> = toSignal(
		this.pendingReads$.pipe(switchMap(pendingRead => this.readFor(pendingRead))),
		{ initialValue: NO_PENDING_IDS },
	);

	public override isPending(projectFinishId: string): Signal<boolean> {
		return computed(() => this.pendingProjectFinishIds().has(projectFinishId));
	}

	public override refresh(): void {
		this.refreshSubject.next();
	}

	private readFor({ projectId, isProjectChange }: PendingRead): Observable<ReadonlySet<string>> {
		/**
		 * `designChangePendingProjectFinishIds` is project-scoped, so with no project the read is skipped
		 * and the set collapses to empty — see the sibling presence service for the defect this prevents.
		 * Nothing is pending when there is no project, so the empty set is the honest answer rather than a
		 * fallback.
		 */
		if (!projectId) {
			return of(NO_PENDING_IDS);
		}

		if (isProjectChange) {
			return this.readPendingIds().pipe(startWith(NO_PENDING_IDS));
		}

		return this.readPendingIds();
	}

	/**
	 * Reports the failure and then emits nothing, so the signal keeps whichever value the trigger left
	 * it on. Returning an empty set here instead would clear a screen that was right.
	 */
	private readPendingIds(): Observable<ReadonlySet<string>> {
		return this.repository.getPendingProjectFinishIds().pipe(
			map((ids): ReadonlySet<string> => new Set(ids)),
			catchError((error: unknown) => {
				this.errorReporter.handleError(error);

				return EMPTY;
			}),
		);
	}
}
